// SVG charts for the dashboards - the mock-ups' sparkline, bar trend with a
// comparison line (one branch) and multi-series line trend (all branches).
// Plain SVG sized to the container, so both themes follow the CSS tokens.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { money } from "./dashData";

const r2 = (n) => Math.round(n * 100) / 100;

// container width, tracked
export function useWidth(fallback = 760) {
  const ref = useRef(null);
  const [w, setW] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => el.clientWidth && setW(el.clientWidth);
    read();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", read);
      return () => window.removeEventListener("resize", read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

export function niceTicks(max, n) {
  if (!(max > 0)) return [0, 1];
  const raw = max / n;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const out = [];
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(r2(v));
  if (out[out.length - 1] < max) out.push(r2(out[out.length - 1] + step));
  return out;
}
const axisFmt = (v, top) => {
  if (top >= 1000000) return v === 0 ? "0" : r2(v / 1000000) + "M";
  if (top >= 10000) return v === 0 ? "0" : Math.round(v / 1000) + "K";
  return money(v);
};

/* ================= sparklines ================= */
export function Sparkline({ vals, w = 150, h = 22, className = "axd-spark" }) {
  if (!vals || vals.length < 2) return <svg className={className} width={w} height={h} aria-hidden="true" />;
  let mn = Math.min(...vals);
  let mx = Math.max(...vals);
  const pad = (mx - mn) * 0.16 || 1;
  mn -= pad;
  mx += pad;
  const step = w / (vals.length - 1);
  const pts = vals.map((v, i) => [r2(i * step), r2(h - ((v - mn) / (mx - mn)) * h)]);
  const d = pts.map((p, i) => (i ? "L" : "M") + p[0] + " " + p[1]).join(" ");
  const a = pts[pts.length - 2];
  const b = pts[pts.length - 1];
  return (
    <svg className={className} width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <path d={d} fill="none" stroke="var(--ghost)" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <path d={`M${a[0]} ${a[1]}L${b[0]} ${b[1]}`} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
      <circle cx={b[0]} cy={b[1]} r="2.6" fill="var(--accent)" stroke="var(--surface)" strokeWidth="2" />
    </svg>
  );
}

export function MicroSpark({ vals, w = 78, h = 20, color }) {
  if (!vals || vals.length < 2) return null;
  let mn = Math.min(...vals);
  let mx = Math.max(...vals);
  const pad = (mx - mn) * 0.18 || 1;
  mn -= pad;
  mx += pad;
  const step = w / (vals.length - 1);
  const d = vals.map((v, i) => (i ? "L" : "M") + r2(i * step) + " " + r2(h - ((v - mn) / (mx - mn)) * h)).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/* ================= tooltip helper ================= */
function useTip() {
  const [i, setI] = useState(null);
  return [i, setI];
}
function placeTip(tipEl, x, W) {
  if (!tipEl) return;
  const tw = tipEl.offsetWidth;
  let left = x + 14;
  if (left + tw > W) left = x - tw - 14;
  tipEl.style.left = Math.max(2, left) + "px";
}

const roundTopRect = (x, y, w, h, r) => {
  if (h <= 0) return "";
  r = Math.min(r, w / 2, h);
  return (
    `M${r2(x)} ${r2(y + h)}V${r2(y + r)}a${r} ${r} 0 0 1 ${r} ${-r}` +
    `h${r2(w - 2 * r)}a${r} ${r} 0 0 1 ${r} ${r}V${r2(y + h)}Z`
  );
};

/* ================= bars + comparison line (one branch) ================= */
export function BarTrend({ x, cur, cmp, extra, cmpName, height = 230, ariaLabel = "Sales trend" }) {
  const [ref, W] = useWidth();
  const [hot, setHot] = useTip();
  const tipRef = useRef(null);
  const H = height;
  const padL = 52;
  const padR = 10;
  const padT = 10;
  const padB = 24;
  const iw = Math.max(80, W - padL - padR);
  const ih = H - padT - padB;
  const mx = Math.max(1, ...cur, ...cmp);
  const ticks = niceTicks(mx, 4);
  const top = ticks[ticks.length - 1];
  const band = iw / Math.max(1, x.length);
  const bw = Math.min(24, band * 0.62);
  const X = (i) => padL + band * i + band / 2;
  const Y = (v) => padT + ih - (v / top) * ih;
  const every = Math.ceil(x.length / Math.max(4, Math.floor(iw / 58)));

  useEffect(() => {
    if (hot !== null) placeTip(tipRef.current, X(hot), W);
  });

  let lastX = -1e9;
  const labels = [];
  x.forEach((s, i) => {
    const forced = i === x.length - 1;
    if (i % every !== 0 && !forced) return;
    if (forced && X(i) - lastX < 46) return;
    lastX = X(i);
    labels.push(
      <text key={i} className="axd-axtext" x={r2(X(i))} y={H - 6} textAnchor="middle">
        {s}
      </text>
    );
  });

  return (
    <div className="axd-plot" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={ariaLabel}>
        {ticks.map((v) => (
          <g key={v}>
            <line className="axd-gridline" x1={padL} x2={W - padR} y1={r2(Y(v))} y2={r2(Y(v))} />
            <text className="axd-axtext" x={padL - 7} y={r2(Y(v) + 3.5)} textAnchor="end">
              {axisFmt(v, top)}
            </text>
          </g>
        ))}
        {cur.map((v, i) => {
          const h = Math.max(0, (v / top) * ih);
          return <path key={i} className={"axd-bar" + (hot === i ? " hot" : "")} d={roundTopRect(X(i) - bw / 2, padT + ih - h, bw, h, 4)} />;
        })}
        {cmp.length > 0 && (
          <path className="axd-cmpline" d={cmp.map((v, i) => (i ? "L" : "M") + r2(X(i)) + " " + r2(Y(v))).join(" ")} />
        )}
        {labels}
        {hot !== null && <line className="axd-crosshair" x1={r2(X(hot))} x2={r2(X(hot))} y1={padT} y2={padT + ih} />}
        {x.map((s, i) => (
          <rect
            key={i}
            className="axd-hit"
            x={r2(padL + band * i)}
            y={padT}
            width={r2(band)}
            height={ih}
            onPointerEnter={() => setHot(i)}
            onPointerMove={() => setHot(i)}
            onPointerLeave={() => setHot(null)}
          />
        ))}
      </svg>
      {hot !== null && (
        <div className="axd-tip" ref={tipRef} style={{ top: Math.max(2, Y(Math.max(cur[hot] || 0, cmp[hot] || 0)) - 12) }}>
          <div className="tt">{x[hot]}</div>
          <div className="tr">
            <span className="key" style={{ background: "var(--accent)" }} />
            <span className="tn">Sales</span>
            <span className="tv">{money(cur[hot])}</span>
          </div>
          <div className="tr">
            <span className="key" style={{ background: "var(--ghost)" }} />
            <span className="tn">{cmpName}</span>
            <span className="tv">{money(cmp[hot] || 0)}</span>
          </div>
          {extra && (
            <div className="tr">
              <span className="key" style={{ background: "transparent" }} />
              <span className="tn">Invoices</span>
              <span className="tv">{extra[hot]}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ================= multi-series lines (all branches) ================= */
export function LineTrend({ x, series, height = 260, ariaLabel = "Sales by branch over time" }) {
  const [ref, W] = useWidth();
  const [hot, setHot] = useTip();
  const tipRef = useRef(null);
  const H = height;
  const padL = 54;
  const padR = series.length && series.length <= 4 ? 92 : 14;
  const padT = 12;
  const padB = 24;
  const iw = Math.max(80, W - padL - padR);
  const ih = H - padT - padB;
  let mx = 1;
  series.forEach((s) => s.v.forEach((v) => v > mx && (mx = v)));
  const ticks = niceTicks(mx, 4);
  const top = ticks[ticks.length - 1];
  const step = x.length > 1 ? iw / (x.length - 1) : iw;
  const X = (i) => padL + step * i;
  const Y = (v) => padT + ih - (v / top) * ih;
  const every = Math.ceil(x.length / Math.max(4, Math.floor(iw / 58)));

  useEffect(() => {
    if (hot !== null) placeTip(tipRef.current, X(hot), W);
  });

  // direct end labels only while <= 4 series are on and they don't collide
  const ends = [];
  if (series.length && series.length <= 4) {
    let lastY = -1e9;
    series
      .map((s) => ({ n: s.n, y: Y(s.v[s.v.length - 1] || 0) }))
      .sort((a, b) => a.y - b.y)
      .forEach((e) => {
        if (e.y - lastY < 14) return;
        lastY = e.y;
        ends.push(e);
      });
  }

  let lastX = -1e9;
  const labels = [];
  x.forEach((s, i) => {
    const forced = i === x.length - 1;
    if (i % every !== 0 && !forced) return;
    if (forced && X(i) - lastX < 46) return;
    lastX = X(i);
    labels.push(
      <text key={i} className="axd-axtext" x={r2(X(i))} y={H - 6} textAnchor="middle">
        {s}
      </text>
    );
  });

  const list = hot === null ? [] : series.slice().sort((a, b) => (b.v[hot] || 0) - (a.v[hot] || 0));
  const tot = list.reduce((a, s) => a + (s.v[hot] || 0), 0);

  return (
    <div className="axd-plot" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={ariaLabel}>
        {ticks.map((v) => (
          <g key={v}>
            <line className="axd-gridline" x1={padL} x2={padL + iw} y1={r2(Y(v))} y2={r2(Y(v))} />
            <text className="axd-axtext" x={padL - 7} y={r2(Y(v) + 3.5)} textAnchor="end">
              {axisFmt(v, top)}
            </text>
          </g>
        ))}
        {series.map((s) => {
          const last = s.v.length - 1;
          return (
            <g key={s.n}>
              <path className="axd-ln" stroke={s.c} d={s.v.map((v, i) => (i ? "L" : "M") + r2(X(i)) + " " + r2(Y(v))).join(" ")} />
              {last >= 0 && (
                <circle cx={r2(X(last))} cy={r2(Y(s.v[last]))} r="4" fill={s.c} stroke="var(--surface)" strokeWidth="2" />
              )}
            </g>
          );
        })}
        {ends.map((e) => (
          <g key={e.n}>
            <line className="axd-leader" x1={r2(padL + iw + 4)} x2={r2(padL + iw + 10)} y1={r2(e.y)} y2={r2(e.y)} />
            <text className="axd-endlbl" x={r2(padL + iw + 14)} y={r2(e.y + 3.5)}>
              {e.n}
            </text>
          </g>
        ))}
        {labels}
        {hot !== null && <line className="axd-crosshair" x1={r2(X(hot))} x2={r2(X(hot))} y1={padT} y2={padT + ih} />}
        {x.map((s, i) => (
          <rect
            key={i}
            className="axd-hit"
            x={r2(X(i) - step / 2)}
            y={padT}
            width={r2(step)}
            height={ih}
            onPointerEnter={() => setHot(i)}
            onPointerMove={() => setHot(i)}
            onPointerLeave={() => setHot(null)}
          />
        ))}
      </svg>
      {hot !== null && (
        <div className="axd-tip wide" ref={tipRef} style={{ top: padT + 6 }}>
          <div className="tt">{x[hot]}</div>
          {list.map((s) => (
            <div className="tr" key={s.n}>
              <span className="key" style={{ background: s.c }} />
              <span className="tn">{s.n}</span>
              <span className="tv">{money(s.v[hot] || 0)}</span>
            </div>
          ))}
          <div className="tr sum">
            <span className="key" style={{ background: "transparent" }} />
            <span className="tn">Group</span>
            <span className="tv">{money(tot)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
